import { FormControl, FormErrorMessage, FormLabel, Input } from '@chakra-ui/react';
import { ChangeEvent, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import {
  DollarIcon,
  IconLeft,
  IconRight,
  IconWrapper,
  PercentIcon
} from '../../PropertyInformation';

interface Props {
  label: string;
  name: string;
  precision?: number;
  type?: 'percent' | 'price' | 'generic';
  defaultValue?: number;
}

const numericTransform = {
  input: (value: number | undefined) =>
    value && Number.isNaN(value) ? '' : typeof value === 'number' ? value.toString() : '',
  output: (e: ChangeEvent<HTMLInputElement>) =>
    Number.isNaN(e.target.valueAsNumber) ? undefined : e.target.valueAsNumber
};

export function NumberFormControl({ label, name, defaultValue, precision, type }: Props) {
  const config = useMemo(() => {
    return {
      step: (1 / Math.pow(10, precision ?? 0)).toPrecision(precision),
      placeholder: precision ? `0.${'0'.repeat(precision)}` : '0'
    };
  }, [precision]);

  const {
    control,
    formState: { errors }
  } = useFormContext<{ [key: string]: number }>();

  return (
    <Controller
      defaultValue={defaultValue}
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => (
        <FormControl isInvalid={!!errors[name]?.message}>
          <FormLabel>{label}</FormLabel>
          <IconWrapper>
            {type === 'price' ? (
              <IconLeft>
                <DollarIcon className={!isNaN(Number(field.value)) ? '' : 'fill-gray-400'} />
              </IconLeft>
            ) : type === 'percent' ? (
              <IconRight>
                <PercentIcon className={!isNaN(Number(field.value)) ? '' : 'fill-gray-400'} />
              </IconRight>
            ) : null}
            <Input
              onChange={(e) => field.onChange(numericTransform.output(e))}
              onBlur={() => field.onBlur()}
              value={numericTransform.input(Number(field.value))}
              type="number"
              placeholder={config.placeholder}
              step={config.step}
            />
          </IconWrapper>
          {error?.message && <FormErrorMessage>{error?.message}</FormErrorMessage>}
        </FormControl>
      )}
    />
  );
}
